import React from 'react';
import { DealDTO, DealStatus } from '../types';
import { dealService } from '../services';
import { ReactComponent as CardIcon } from '../icons/card.svg';
import { ReactComponent as AvatarIcon } from '../icons/avatar.svg';
import { ReactComponent as InfoIcon } from '../icons/info.svg';
import { ReactComponent as ShareIcon } from '../icons/share.svg';
import { ReactComponent as BillIcon } from '../icons/bill.svg';
import { ReactComponent as DownloadIcon } from '../icons/download.svg';
import { ReactComponent as UserIcon } from '../icons/user.svg';

interface DealDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  deal: DealDTO | null;
  onDealUpdate?: () => void;
}

const DealDetailsModal: React.FC<DealDetailsModalProps> = ({
  isOpen,
  onClose,
  deal,
  onDealUpdate
}) => {
  const [notification, setNotification] = React.useState<{ message: string; isVisible: boolean }>({ message: '', isVisible: false });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
  const [showConfirmDealConfirmation, setShowConfirmDealConfirmation] = React.useState(false);
  const [showRefundConfirmation, setShowRefundConfirmation] = React.useState(false);
  const [sendingEmail, setSendingEmail] = React.useState(false);
  const [emailSent, setEmailSent] = React.useState(false);

  if (!deal) return null;

  const payerStatusText =
    deal.payerRejectedAt != null
      ? 'Отменено покупателем'
      : deal.payerConfirmedAt != null
        ? 'Подтверждено покупателем'
        : 'Ожидание подтверждения покупателя';
  const recipientStatusText =
    deal.recipientRejectedAt != null
      ? 'Вы отменили сделку'
      : deal.recipientConfirmedAt != null
        ? 'Вы подтвердили сделку'
        : 'Ожидание вашего подтверждения';

  const getStatusText = (status: DealStatus): string => {
    switch (status) {
      case DealStatus.New: return 'Новая';
      case DealStatus.Registered: return 'Зарегистрирована';
      case DealStatus.Wait: return 'В процессе';
      case DealStatus.Paid: return 'Оплачена';
      case DealStatus.Finished: return 'Завершена';
      case DealStatus.Rejected: return 'Отклонена';
      case DealStatus.Refunded: return 'Возвращена';
      case DealStatus.Deleted: return 'Удалена';
      default: return status;
    }
  };

  const getStatusColor = (status: DealStatus): string => {
    switch (status) {
      case DealStatus.Paid:
      case DealStatus.Finished:
        return 'bg-green-500';
      case DealStatus.Wait:
        return 'bg-yellow-500';
      case DealStatus.Rejected:
        return 'bg-red-500';
      case DealStatus.Refunded:
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };

  const formatAmount = (amount: number): string => {
    const amountInRubles = amount / 100;
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amountInRubles);
  };

  const formatCardNumber = (maskedCardNumber: string): string => {
    if (!maskedCardNumber) return '**** **** **** ****';
    
    // Убираем все звездочки и оставляем только цифры
    const digits = maskedCardNumber.replace(/\*/g, '');
    
    // Если цифр меньше 8, возвращаем как есть
    if (digits.length < 8) return maskedCardNumber;
    
    // Форматируем номер карты в виде "1234 **** **** 1234"
    if (digits.length >= 8) {
      const firstFour = digits.slice(0, 4);
      const lastFour = digits.slice(-4);
      return `${firstFour} **** **** ${lastFour}`;
    }
    
    // Если цифр меньше 8, возвращаем как есть
    return maskedCardNumber;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    // Если дата сегодня, показываем только время
    if (diffInHours < 24 && date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    // Если дата вчера, показываем "вчера, время"
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `вчера, ${date.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })}`;
    }
    
    // Иначе показываем дату и время в формате "15 авг., 12:45"
    const months = [
      'янв.', 'фев.', 'мар.', 'апр.', 'май', 'июн.',
      'июл.', 'авг.', 'сен.', 'окт.', 'ноя.', 'дек.'
    ];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const time = date.toLocaleTimeString('ru-RU', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    
    return `${day} ${month}, ${time}`;
  };

  // Функция для показа уведомлений
  const showNotification = (message: string) => {
    setNotification({ message, isVisible: true });
    // Автоматически скрываем уведомление через 5 секунд
    setTimeout(() => {
      setNotification(prev => ({ ...prev, isVisible: false }));
    }, 5000);
  };



  const copyPaymentLink = async () => {
    try {
      // Здесь должна быть логика получения ссылки на оплату
      const paymentUrl = `${window.location.origin}/pay/${deal.id}`;
      await navigator.clipboard.writeText(paymentUrl);
      showNotification('Ссылка скопирована в буфер обмена');
    } catch (error) {
      console.error('Failed to copy link:', error);
      showNotification('Не удалось скопировать ссылку');
    }
  };

  const handleDeleteDeal = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDeleteDeal = async () => {
    try {
      const success = await dealService.deleteDeal(deal.id);
      if (success) {
        showNotification('Сделка успешно удалена');
        setShowDeleteConfirmation(false);
        onDealUpdate?.(); // Обновляем список сделок в родительском компоненте
        onClose();
      } else {
        showNotification('Не удалось удалить сделку');
      }
    } catch (error) {
      console.error('Failed to delete deal:', error);
      showNotification('Ошибка при удалении сделки');
    }
  };

  const handleCancelDeleteDeal = () => {
    setShowDeleteConfirmation(false);
  };

  const handleConfirmDeal = () => {
    setShowConfirmDealConfirmation(true);
  };

  const handleConfirmDealAction = async () => {
    try {
      const success = await dealService.confirmDeal(deal.id);
      if (success) {
        showNotification('Сделка подтверждена и выплата инициирована');
        setShowConfirmDealConfirmation(false);
        onDealUpdate?.(); // Обновляем список сделок в родительском компоненте
        onClose();
      } else {
        showNotification('Не удалось подтвердить сделку');
      }
    } catch (error) {
      console.error('Failed to confirm deal:', error);
      showNotification('Ошибка при подтверждении сделки');
    }
  };

  const handleCancelConfirmDeal = () => {
    setShowConfirmDealConfirmation(false);
  };

  const handleRefundDeal = () => {
    setShowRefundConfirmation(true);
  };

  const handleConfirmRefundDeal = async () => {
    try {
      const success = await dealService.refundDeal(deal.id);
      if (success) {
        showNotification('Возврат по сделке успешно оформлен');
        setShowRefundConfirmation(false);
        onDealUpdate?.(); // Обновляем список сделок в родительском компоненте
        onClose();
      } else {
        showNotification('Не удалось оформить возврат');
      }
    } catch (error) {
      console.error('Failed to refund deal:', error);
      showNotification('Ошибка при оформлении возврата');
    }
  };

  const handleCancelRefundDeal = () => {
    setShowRefundConfirmation(false);
  };

  const handleSendPayerConfirmEmail = async () => {
    setSendingEmail(true);
    setEmailSent(false);
    try {
      const ok = await dealService.sendPayerConfirmEmail(deal.id);
      if (ok) {
        setEmailSent(true);
        showNotification('Письмо со ссылками отправлено на почту покупателя');
      } else {
        showNotification('Не удалось отправить письмо');
      }
    } catch {
      showNotification('Ошибка при отправке письма');
    } finally {
      setSendingEmail(false);
    }
  };

  const downloadReceipt = (type: 'cash' | 'npd') => {
    // Здесь должна быть логика скачивания чека
    console.log(`Downloading ${type} receipt for deal:`, deal.id);
    showNotification(`Скачивание ${type === 'cash' ? 'кассового чека' : 'чека НПД'}...`);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 transition-opacity ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className={`fixed bottom-0 left-0 right-0 bg-[#141826] rounded-t-3xl w-full max-h-screen flex flex-col transform transition-transform duration-300 ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}>
          {/* Header */}
          <div className="text-white text-left p-4 relative flex-shrink-0">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
            >
              ✕
            </button>
            <div className="flex items-start">
              <div className="flex-1 pt-12">
                                 <h3 className="text-lg">{deal.purpose}</h3>
                <p className="text-slate-400 text-sm mt-1">{formatDate(deal.createdAt)}</p>
              </div>
              <div className="text-right pt-12 ml-auto">
                                 <div className="text-xl">{formatAmount(deal.amount)}</div>
                <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(deal.status)}`}>
                  {getStatusText(deal.status)}
                </div>
              </div>
            </div>
          </div>
          
          {/* Body */}
          <div className="text-white px-4 pb-6 overflow-y-auto">
            {/* Transaction Details */}
            <div className="mb-6">
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                    <CardIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                                         <div className="text-white">
                       {formatCardNumber(deal.card?.maskedCardNumber || '')}
                     </div>
                    <div className="text-slate-400 text-sm">
                      Вывод на банковскую карту
                    </div>
                  </div>
                </div>
              </div>
            </div>

                         {/* Transaction History */}
             <div className="mb-6">
               <h4 className="text-lg mb-4">История сделки</h4>
               <div className="space-y-4">
                {deal.history && deal.history.length > 0 ? (
                  deal.history.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="text-slate-300 text-sm">{item.action}</div>
                      </div>
                      <div className="text-slate-400 text-xs">
                        {formatDate(item.createdAt)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-slate-400 text-sm text-center py-4">
                    История сделки пока пуста
                  </div>
                )}
              </div>
            </div>

            {/* Buyer Information */}
            {deal.receiptEmail && (
              <div className="mb-6">
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                      <UserIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-white">Покупатель</div>
                      <div className="text-slate-400 text-sm">{deal.receiptEmail}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payer / Recipient status (for Paid deals) */}
            {deal.status === DealStatus.Paid && (
              <div className="mb-6 space-y-3">
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4">
                  <div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Статус со стороны покупателя</div>
                  <div className="text-white">{payerStatusText}</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4">
                  <div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Ваш статус (получатель)</div>
                  <div className="text-white">{recipientStatusText}</div>
                </div>
                <button
                  onClick={handleSendPayerConfirmEmail}
                  disabled={sendingEmail || !deal.receiptEmail}
                  className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-slate-600 text-white hover:bg-slate-500 disabled:opacity-50"
                >
                  {sendingEmail ? 'Отправка...' : emailSent ? 'Письмо отправлено' : 'Отправить письмо со ссылками на подтверждение покупателю'}
                </button>
              </div>
            )}

                         {/* Documents */}
             {(deal.platformCommissionBill || deal.npdBill) && (
               <div className="mb-6">
                 <h4 className="text-lg mb-4">Документы</h4>
                 <div className="space-y-3">
                  {/* Cash Receipt */}
                  {deal.platformCommissionBill && (
                    <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                            <BillIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-white">Кассовый чек</div>
                            <div className="text-slate-400 text-sm">Скачайте чек</div>
                          </div>
                        </div>
                        <button
                          onClick={() => window.open(deal.platformCommissionBill, '_blank')}
                          className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-white/90 transition-colors"
                        >
                          <DownloadIcon className="w-4 h-4 text-gray-800" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* NPD Receipt */}
                  {deal.npdBill && (
                    <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                            <BillIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-white">Чек НПД</div>
                            <div className="text-slate-400 text-sm">Скачайте чек</div>
                          </div>
                        </div>
                        <button
                          onClick={() => window.open(deal.npdBill, '_blank')}
                          className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-white/90 transition-colors"
                        >
                          <DownloadIcon className="w-4 h-4 text-gray-800" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
             )}

            {/* Action Buttons */}
            <div className="flex-shrink-0 pb-6">
              {deal.status === DealStatus.New || deal.status === DealStatus.Wait || deal.status === DealStatus.Registered ? (
                <div className="space-y-3">
                  <button
                    onClick={copyPaymentLink}
                    className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95"
                  >
                    <span>Скопировать ссылку</span>
                  </button>
                  
                  <button
                    onClick={handleDeleteDeal}
                    className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-slate-700 text-white hover:bg-slate-600 active:scale-95"
                  >
                    <span>Удалить</span>
                  </button>
                </div>
              ) : deal.status === DealStatus.Paid ? (
                <div className="space-y-3">
                  <button
                    onClick={handleConfirmDeal}
                    className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95"
                  >
                    <span>Подтвердить</span>
                  </button>
                  
                  <button
                    onClick={handleRefundDeal}
                    className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-slate-700 text-white hover:bg-white/90 active:scale-95"
                  >
                    <span>Сделать возврат</span>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCancelDeleteDeal}
          />
          
          {/* Modal */}
          <div className="relative bg-[#141826] rounded-2xl w-full max-w-sm mx-4 p-6">
            {/* Header */}
            <div className="text-white text-center mb-6">
              <h3 className="text-2xl mb-2">Удалить сделку?</h3>
              <p className="text-slate-400 text-sm">
                Вы уверены, что хотите удалить эту сделку?
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleCancelDeleteDeal}
                className="flex-1 py-3 px-4 rounded-full bg-slate-700 text-white hover:bg-slate-600 transition-colors"
              >
                Отменить
              </button>
              <button
                onClick={handleConfirmDeleteDeal}
                className="flex-1 py-3 px-4 rounded-full bg-white text-gray-800 hover:bg-white/90 transition-colors"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Deal Confirmation Modal */}
      {showConfirmDealConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCancelConfirmDeal}
          />
          
          {/* Modal */}
          <div className="relative bg-[#141826] rounded-2xl w-full max-w-sm mx-4 p-6">
            {/* Header */}
            <div className="text-white text-center mb-6">
              <h3 className="text-2xl mb-2">Подтвердить сделку?</h3>
              <p className="text-slate-400 text-sm">
                Вы уверены, что хотите подтвердить эту сделку?
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleCancelConfirmDeal}
                className="flex-1 py-3 px-4 rounded-full bg-slate-700 text-white hover:bg-slate-600 transition-colors"
              >
                Отменить
              </button>
              <button
                onClick={handleConfirmDealAction}
                className="flex-1 py-3 px-4 rounded-full bg-white text-gray-800 hover:bg-white/90 transition-colors"
              >
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Deal Confirmation Modal */}
      {showRefundConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCancelRefundDeal}
          />
          
          {/* Modal */}
          <div className="relative bg-[#141826] rounded-2xl w-full max-w-sm mx-4 p-6">
            {/* Header */}
            <div className="text-white text-center mb-6">
              <h3 className="text-2xl mb-2">Сделать возврат?</h3>
              <p className="text-slate-400 text-sm">
                Вы уверены, что хотите сделать возврат по этой сделке?
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleCancelRefundDeal}
                className="flex-1 py-3 px-4 rounded-full bg-slate-700 text-white hover:bg-slate-600 transition-colors"
              >
                Отменить
              </button>
              <button
                onClick={handleConfirmRefundDeal}
                className="flex-1 py-3 px-4 rounded-full bg-white text-gray-800 hover:bg-white/90 transition-colors"
              >
                Сделать возврат
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Panel */}
      {notification.isVisible && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setNotification(prev => ({ ...prev, isVisible: false }))}
          />
          
          {/* Notification Panel */}
          <div className="relative bg-[#141826] rounded-t-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="text-white text-left p-4 relative border-b border-white/10">
              <button
                onClick={() => setNotification(prev => ({ ...prev, isVisible: false }))}
                className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
              >
                ✕
              </button>
              <h3 className="text-2xl">Уведомление</h3>
            </div>
            
            {/* Body */}
            <div className="text-white p-4">
              <div className="text-center py-6">
                <div className="text-white text-lg mb-6">
                  {notification.message}
                </div>
                
                <button
                  onClick={() => setNotification(prev => ({ ...prev, isVisible: false }))}
                  className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95"
                >
                  <span>Ок</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DealDetailsModal;
